// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconLogOut from "@pace/XDL/Icons/IconLogOut";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { color, createLinkIconStyles, mergeWithCss, safeJsonParse } from "@pace/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
                  openInNewWindow?: boolean;
              }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subTitle?: TypographyPresentationProps;
        shipToInfo?: TypographyPresentationProps;
        link?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
    };
}

const sharedSectionTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    size: 14,
    css: css`
        flex-basis: 100%;
        margin-bottom: 8px;
    `,
};

const miniMyAccountStyles: MiniMyAccountStyles = {
    titleWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: space-between;
            padding: 16px 16px 0 16px;
            margin-bottom: 14px;
        `,
    },
    title: {
        variant: "h5",
        css: css`
            padding-bottom: 8px;
            margin-bottom: 0;
            border-bottom: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                padding: 0 17px;
                margin-bottom: 12px;
            `,
        },
        title: sharedSectionTitleStyle,
        link: {
            typographyProps: {
                size: 12,
                weight: fontConfig.weight.bold,
                lineHeight: "17px",
                css: css`
                    letter-spacing: 0;
                    margin-bottom: 0;
                `,
            },
        },
        linkWrapper: {
            css: css`
                flex-basis: calc(50% - 12px);
                margin-bottom: 12px;
            `,
        },
    },
    personalInfo: {
        wrapper: {
            css: css`
                padding: 0 17px;
                display: flex;
                column-gap: 16px;
                justify-content: space-between;
                flex-wrap: wrap;
                margin-bottom: 24px;
            `,
        },
        infoWrapper: {
            css: css`
                flex: 0 0 calc(50% - 8px);
            `,
        },
        title: sharedSectionTitleStyle,
        subTitle: {
            size: 12,
            lineHeight: "17px",
            forwardAs: "h6",
            weight: fontConfig.weight.bold,
            css: css`
                flex-basis: 100%;
                margin-bottom: 4px;
            `,
        },
        shipToInfo: {
            size: 12,
            weight: fontConfig.weight.regular,
            lineHeight: "13px",
            css: css`
                display: block;
                width: 100%;
            `,
        },
        link: {
            ellipsis: true,
            lineHeight: "16px",
            color: "text.link",
            size: "12px",
            weight: 600,
            css: css`
                display: flex;
                margin-bottom: 5px;
                letter-spacing: 0;
                align-items: center;
                ${IconWrapper} {
                    margin-right: 4px;
                    opacity: 0.5;
                }
            `,
        },
    },
    actionSection: {
        wrapper: {
            css: css`
                border-top: 1px solid ${color("common.border")};
                padding: 17px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                width: 100%;
            `,
        },
        signOutButton: {
            ...createLinkIconStyles(IconLogOut, {
                icon: {
                    position: "right",
                },
                css: css`
                    margin-left: auto;
                `,
            }),
        },
    },
};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const history = useHistory();
    const { currentBillTo, displayChangeCustomerLink, isPunchOutSession, changeCustomerUrl, warehouseInformation } =
        useAppSelector(state => {
            const session = getSession(state);
            return {
                currentBillTo: getBillToState(state, session.billToId).value,
                displayChangeCustomerLink: session.displayChangeCustomerLink,
                isPunchOutSession: getIsPunchOutSession(state),
                changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
                warehouseInformation: safeJsonParse(session.properties["headerWarehouseInformation"]) || {},
            };
        });

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link
                                        onClick={() => {
                                            hideAll();
                                        }}
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>{translate("Account Dashboard")}</Typography>
            </StyledWrapper>
            {linksSection}
            <StyledWrapper {...styles.personalInfo?.wrapper}>
                <Typography {...styles.personalInfo?.title}>{translate("Profile Information")}</Typography>
                <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                    <Typography {...styles.personalInfo?.subTitle}>{translate("Account")}</Typography>
                    <Typography {...styles.personalInfo?.shipToInfo}>
                        {currentBillTo?.customerNumber} - {currentBillTo?.customerName}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                    <Typography {...styles.personalInfo?.subTitle}>{translate("Branch")}</Typography>
                    <Typography {...styles.personalInfo?.shipToInfo}>{warehouseInformation.Name} - {warehouseInformation.Description} - {warehouseInformation.AddressToDisplay}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                {displayChangeCustomerLink && (
                    <Button
                        variant="primary"
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                            hideAll();
                        }}
                        aria-label="Change Account"
                    >
                        {translate("Change Account")}
                    </Button>
                )}
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        hideAll();
                    }}
                    aria-label="Log Out"
                    data-test-selector="headerLogOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Log Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
